import type { FunctionComponent, MouseEventHandler } from 'react';
import { useCallback } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { ComponentWrapper } from '@trello/component-wrapper';
import { forNamespace } from '@trello/legacy-i18n';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';
import {
  useUpgradePromptRules,
  WithEnterpriseManagedOverride,
} from 'app/src/components/UpgradePrompts';

import styles from './PrivateBoardInfo.less';

const format = forNamespace('private board limit info', {
  shouldEscapeStrings: false,
});

interface PrivateBoardInfoProps {
  orgId: string;
  onMouseEnter?: MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: MouseEventHandler<HTMLDivElement>;
  onClickOpenPlanSelection?: () => void;
}

interface PrivateBoardInfoTextProps {
  orgId: string;
  openPlanSelection: () => void;
  onClickOpenPlanSelection?: () => void;
  isEligible: boolean;
}

// It seems like this function is needed because of a weird JSX parsing edge case.
const PrivateBoardInfoText: FunctionComponent<PrivateBoardInfoTextProps> = ({
  orgId,
  openPlanSelection,
  onClickOpenPlanSelection,
  isEligible,
}) => {
  const openPlanSelectionWithAnalytics = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'bcUpgradePrompt',
      source: 'teamScreen',
      containers: {
        organization: {
          id: orgId,
        },
      },
      attributes: {
        promptId: 'privateTeamBoardToolTipUpgradeLink',
        isFreeTrial: isEligible,
      },
    });

    openPlanSelection();
    onClickOpenPlanSelection?.();
  }, [openPlanSelection, onClickOpenPlanSelection, orgId, isEligible]);

  return (
    <>
      {format('private board gtm')}
      <div>
        <a key="upgrade-link" href="#" onClick={openPlanSelectionWithAnalytics}>
          <WithEnterpriseManagedOverride>
            {isEligible ? format('start free trial') : format('upgrade now')}
          </WithEnterpriseManagedOverride>
        </a>
      </div>
    </>
  );
};

export const PrivateBoardInfo: FunctionComponent<PrivateBoardInfoProps> = ({
  onMouseEnter,
  onMouseLeave,
  onClickOpenPlanSelection,
  orgId,
}) => {
  const { openPlanSelection } = useUpgradePromptRules(orgId);
  const { isEligible } = useFreeTrialEligibilityRules(orgId);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={styles.whiteBox}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <PrivateBoardInfoText
        orgId={orgId}
        openPlanSelection={openPlanSelection}
        isEligible={isEligible}
        onClickOpenPlanSelection={onClickOpenPlanSelection}
      />
    </div>
  );
};

export const PrivateBoardInfoConnected: FunctionComponent<
  PrivateBoardInfoProps
> = (props) => (
  <ComponentWrapper>
    <PrivateBoardInfo {...props} />
  </ComponentWrapper>
);
